/**
 * Pure audio policy (plan P2): rate policy, voice selection with hysteresis and crossfades, pan and
 * distance gain, and the radio / commentary play-position rules. No DOM, no Web Audio.
 * Distance gain and panning are stylized choices (no Doppler), not acoustics.
 */
import type { CommentaryLine, RadioClip } from './types.ts';
import { MAX_VOICES } from './engine-dsp.ts';

export const VOICE_RATE_MAX = 1.5;
export const DRIFT_S = 0.25;
export const NEAREST_N = 6;
/** a challenger must be this much nearer than the member it replaces */
export const HYSTERESIS = 0.8;
export const CROSSFADE_S = 0.25;

export interface PolicyInput { playing: boolean; effectiveRate: number; buffering: boolean; dragging: boolean; hidden: boolean }
export interface RatePolicy {
  engines: 'voices' | 'off';
  /** music and crowd: on at every rate while playing (300 ms fade on the off states) */
  ambience: boolean;
  /** radio element may play (the driver still needs second granularity and a src_url) */
  radioAudio: boolean;
  commentary: boolean;
}

/**
 * plan P2 table: paused, buffering, scrub drag and a hidden tab are all "off". A Director hold (playing at rate 0:
 * the 12 s title intro, the end card) keeps music, crowd and commentary (the intro is spoken there) but no engines or radio.
 */
export function ratePolicy(s: PolicyInput): RatePolicy {
  if (!s.playing || s.buffering || s.dragging || s.hidden) {
    return { engines: 'off', ambience: false, radioAudio: false, commentary: false };
  }
  if (!(s.effectiveRate > 0)) return { engines: 'off', ambience: true, radioAudio: false, commentary: true };
  return s.effectiveRate <= VOICE_RATE_MAX
    ? { engines: 'voices', ambience: true, radioAudio: true, commentary: true }
    : { engines: 'off', ambience: true, radioAudio: false, commentary: true };
}

/** screen x (-1..1) to a pan value, clamped */
export const panOf = (sx: number): number => Math.min(Math.max(sx, -1), 1);

/** gain falls with screen-space distance: zoom is pixels per metre, so zooming in makes cars nearer */
export function distanceGain(distM: number, zoom: number): number {
  const px = Math.max(distM, 0) * Math.max(zoom, 0);
  return 1 / (1 + px / 400);
}

/** race time -> position in the radio file, or null outside [t_play, t_play + dur) */
export function radioTarget(raceT: number, c: Pick<RadioClip, 't_play' | 'offset_s' | 'dur'>): number | null {
  const x = raceT - c.t_play;
  return x >= 0 && x < c.dur ? c.offset_s + x : null;
}

/** presentation time -> position in the line's audio, or null outside [t_pres, t_pres + dur) */
export function commentaryTarget(presT: number, l: Pick<CommentaryLine, 't_pres' | 'dur'>): number | null {
  const x = presT - l.t_pres;
  return x >= 0 && x < l.dur ? x : null;
}

export interface Slot { car: number; level: number }

/**
 * Chooses which cars get a full voice: the nearest NEAREST_N, replaced only when a challenger is
 * HYSTERESIS times nearer. A car that leaves fades out in its slot over CROSSFADE_S, a new one fades
 * in; a slot is reused only once it is silent.
 */
export class VoiceSelector {
  readonly slots: Slot[] = Array.from({ length: MAX_VOICES }, () => ({ car: -1, level: 0 }));
  private members = new Set<number>();

  update(dt: number, cands: readonly { idx: number; dist: number }[], voicesOn: boolean): readonly Slot[] {
    const dist = new Map<number, number>();
    for (const c of cands) dist.set(c.idx, c.dist);
    const sorted = [...cands].sort((a, b) => a.dist - b.dist || a.idx - b.idx);
    if (!voicesOn) this.members.clear();
    else {
      for (const m of this.members) if (!dist.has(m)) this.members.delete(m);
      for (const c of sorted) {
        if (this.members.size >= NEAREST_N) break;
        if (!this.members.has(c.idx)) this.members.add(c.idx);
      }
      for (const c of sorted) {
        if (this.members.has(c.idx)) continue;
        let worst = -1, wd = -Infinity;
        for (const m of this.members) { const d = dist.get(m) ?? Infinity; if (d > wd) { wd = d; worst = m; } }
        if (worst >= 0 && c.dist < wd * HYSTERESIS) { this.members.delete(worst); this.members.add(c.idx); } else break;
      }
    }
    const step = Math.max(dt, 0) / CROSSFADE_S;
    for (const s of this.slots) {
      if (s.car < 0) continue;
      s.level = this.members.has(s.car) ? Math.min(1, s.level + step) : Math.max(0, s.level - step);
      if (s.level <= 0) s.car = -1;
    }
    for (const m of this.members) {
      if (this.slots.some((s) => s.car === m)) continue;
      const free = this.slots.find((s) => s.car < 0);
      if (free) { free.car = m; free.level = Math.min(1, step); }
    }
    return this.slots;
  }
}
